/**
 * Course room helpers.
 *
 * The room is one page rendered for two audiences. These functions hold the
 * decisions that differ between them — which tabs exist, what a piece of work
 * is called right now, whether a deadline has passed — so the component tree
 * stays about layout and the rules stay testable.
 */

export const ROOM_TABS = [
  // Everything a course teaches lives in the classwork stream on the left, so
  // the tabs are only the things that describe the course as a whole.
  { id: 'overview', label: 'Overview' },
  { id: 'materials', label: 'Course materials' },
  { id: 'live', label: 'Live' },
  { id: 'students', label: 'Students', editorOnly: true },
  { id: 'gradebook', label: 'Gradebook', editorOnly: true },
];

export function tabsForRole(role) {
  const isEditor = role === 'editor';
  return ROOM_TABS.filter((tab) => isEditor || !tab.editorOnly);
}

/**
 * The three things an instructor can add to a course.
 *
 * A material is something to read or watch; an assignment is handed in and
 * marked; a quiz is sat. They are stored in three different collections —
 * submissions and transcripts point at assignment and exam ids — but they are
 * one list to everybody using the course.
 */
export const CLASSWORK_KINDS = [
  { id: 'material', label: 'Material', noun: 'Material' },
  { id: 'assignment', label: 'Assignment', noun: 'Assignment' },
  { id: 'quiz', label: 'Quiz', noun: 'Quiz' },
];

export function kindLabel(kind) {
  return (CLASSWORK_KINDS.find((k) => k.id === kind) || {}).label || 'Item';
}

/**
 * Merge lessons, assignments and exams into one ordered classwork stream,
 * grouped under their module. Order within a module puts the instructor's
 * explicit `order` first and falls back to creation time, so items added from
 * different surfaces still land somewhere sensible.
 *
 * A group is a `course_modules` row. The API and the database still call these
 * topics; only what a person reads says "module", so the two vocabularies were
 * reconciled without a migration.
 */
export function buildClasswork({ lessons, assignments, exams, modules }) {
  const items = [
    ...(lessons || []).map((row) => ({ ...row, kind: 'material', itemId: row.id })),
    ...(assignments || []).map((row) => ({ ...row, kind: 'assignment', itemId: row.id })),
    ...(exams || []).map((row) => ({ ...row, kind: 'quiz', itemId: row.id })),
  ];

  const byOrder = [...(modules || [])].sort((a, b) => (a.order || 0) - (b.order || 0));
  const sortItems = (list) => list.sort((a, b) => {
    const order = (a.order || 0) - (b.order || 0);
    if (order !== 0) return order;
    return String(a.created_at || '').localeCompare(String(b.created_at || ''));
  });

  const groups = byOrder.map((module) => ({
    id: module.id,
    title: module.title || 'Untitled module',
    summary: module.summary || '',
    isTopic: true,
    items: sortItems(items.filter((item) => item.module_id === module.id)),
  }));

  const loose = sortItems(items.filter(
    (item) => !item.module_id || !(modules || []).some((m) => m.id === item.module_id),
  ));
  if (loose.length) {
    groups.push({ id: '', title: 'Classwork', summary: '', isTopic: false, items: loose });
  }
  return groups;
}

/** Every classwork item as one flat, stream-ordered list. */
export function flatClasswork(groups) {
  return (groups || []).flatMap((group) => group.items || []);
}

/** Total taught minutes across the lessons currently visible. */
export function totalDuration(lessons) {
  return (lessons || []).reduce((sum, lesson) => sum + (Number(lesson?.duration_minutes) || 0), 0);
}

/** A student's submission for one assignment or exam, if they made one. */
export function submissionFor(submissions, referenceId) {
  return (submissions || []).find((item) => item.reference_id === referenceId) || null;
}

const DAY = 24 * 60 * 60 * 1000;

/**
 * How a due date should read right now.
 *
 * Returns a tone as well as a label so the same judgement drives the wording
 * and the colour — an overdue assignment should never be styled as merely
 * upcoming because two call sites disagreed.
 */
export function dueState(dueDate, { now = new Date(), submitted = false } = {}) {
  const text = String(dueDate || '').trim();
  if (!text) return { tone: 'none', label: 'No deadline' };
  const due = new Date(text);
  if (Number.isNaN(due.getTime())) return { tone: 'none', label: 'No deadline' };

  const formatted = due.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  if (submitted) return { tone: 'done', label: `Due ${formatted}` };

  const diff = due.getTime() - now.getTime();
  if (diff < 0) return { tone: 'overdue', label: `Overdue — was due ${formatted}` };
  if (diff <= DAY) return { tone: 'soon', label: `Due today, ${formatted}` };
  if (diff <= 3 * DAY) return { tone: 'soon', label: `Due ${formatted}` };
  return { tone: 'upcoming', label: `Due ${formatted}` };
}

/** Sessions split into the three states the Live tab renders. */
export function groupSessions(sessions) {
  const safe = Array.isArray(sessions) ? sessions : [];
  return {
    live: safe.filter((s) => s.status === 'live'),
    upcoming: safe.filter((s) => s.status === 'upcoming'),
    past: safe.filter((s) => s.status === 'completed' || s.status === 'cancelled'),
  };
}

/**
 * Counts shown on the tab strip. A tab with nothing in it still renders, so an
 * instructor can add the first item — the count just stays absent.
 */
export function tabCounts(data) {
  const isEditor = data?.role === 'editor';
  return {
    materials: (data?.resources || []).filter((r) => !(r.item_id || r.assignment_id)).length || undefined,
    live: (data?.sessions || []).filter((s) => s.status === 'live').length
      || (data?.sessions || []).length || undefined,
    students: isEditor ? (data?.students || []).length || undefined : undefined,
  };
}

/** Lessons a student has finished, as a percentage of what they can see. */
export function progressPercent(completedLessons, lessons) {
  const total = (lessons || []).length;
  if (!total) return 0;
  const done = (completedLessons || []).filter((id) => (lessons || []).some((l) => l.id === id)).length;
  return Math.round((done / total) * 100);
}
